import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { toast } from "@/lib/toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tag, Loader2 } from "lucide-react";

const money = (c) => (c == null ? "—" : `$${(c / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`);

// Buyer-side "Make Offer" dialog. For a variant listing the offer is tied to the chosen option.
export function MakeOfferModal({ open, onClose, listing, variant }) {
  const navigate = useNavigate();
  const [amount, setAmount] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const refCents = variant ? Math.round(variant.price * 100) : Math.round((listing?.price || 0) * 100);

  const submit = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error("Enter an offer amount."); return; }
    if (Math.round(amt * 100) >= refCents) { toast.error("That's at or above the asking price — use Buy Now instead."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/listings/${listing.listing_id}/offers`, {
        amount: amt,
        message: message.trim() || undefined,
        variant_id: listing.has_variants ? (variant?.vid || undefined) : undefined,
      });
      if (data.auto_accepted) {
        toast.success("Accepted instantly — reserved for you for 24h. Finish checkout from your Offers.");
        onClose(); navigate("/offers");
      } else if (data.auto_declined) {
        toast.error(data.notice || "That offer is below the seller's minimum. Try a higher amount.");
      } else {
        toast.success("Offer sent — we'll let you know when the seller responds.");
        onClose(); navigate("/offers");
      }
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-surface border border-sniper max-w-md" data-testid="make-offer-modal">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase flex items-center gap-2 text-foreground">
            <Tag className="w-5 h-5 text-olive-bright" /> Make an offer
          </DialogTitle>
          <DialogDescription className="font-mono text-xs text-muted-foreground">
            {listing?.title}
          </DialogDescription>
        </DialogHeader>

        {listing?.has_variants && variant && (
          <div className="flex items-center justify-between border border-sniper bg-obsidian/50 px-3 py-2" data-testid="offer-variant-line">
            <span className="font-body text-sm text-foreground">{variant.label}</span>
            <span className="font-mono text-xs text-muted-foreground">Asking {money(refCents)}</span>
          </div>
        )}

        <label className="block">
          <span className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground">Your offer ($)</span>
          <input
            type="number" min="0" step="1" autoFocus value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder={refCents ? `Below ${money(refCents)}` : "Amount"}
            data-testid="offer-amount-input"
            className="mt-1 w-full bg-input border border-sniper focus:border-olive outline-none px-3 py-2.5 text-foreground font-heading text-xl tabular-nums"
          />
        </label>

        <label className="block">
          <span className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground">Message (optional)</span>
          <textarea
            value={message} onChange={(e) => setMessage(e.target.value)} rows={2} maxLength={500}
            placeholder="Cash ready, can pick up this week…"
            data-testid="offer-message-input"
            className="mt-1 w-full bg-input border border-sniper focus:border-olive outline-none px-3 py-2 text-foreground font-body text-sm resize-none"
          />
        </label>

        <div className="flex gap-2 pt-1">
          <button onClick={onClose} disabled={busy} data-testid="offer-cancel-btn"
            className="flex-1 border border-sniper hover:border-foreground text-secondary-foreground hover:text-foreground font-mono uppercase text-sm py-2.5 transition-colors">
            Cancel
          </button>
          <button onClick={submit} disabled={busy} data-testid="offer-submit-btn"
            className="flex-1 flex items-center justify-center gap-2 bg-olive hover:bg-olive-bright disabled:opacity-50 text-white font-heading font-bold uppercase py-2.5 transition-colors">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send offer"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default MakeOfferModal;
